import APP_CONFIG from '../config/appConfig';

const API_BASE_URL = APP_CONFIG.API.BASE_URL;

/**
 * Authentication API Service for user management
 */
class AuthAPI {
  constructor() {
    this.baseURL = `${API_BASE_URL}/auth`;
  }

  /**
   * Make HTTP request with error handling
   */
  async makeRequest(url, options = {}) {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      ...options,
    };

    // Add authorization header if token exists
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }

      // Handle empty responses
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Auth API Request failed:', error);
      
      // 📊 PRODUCTION ERROR TRACKING
      const errorInfo = {
        timestamp: new Date().toISOString(),
        url: url,
        method: config.method || 'GET',
        userAgent: navigator.userAgent,
        isMobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        error: error.message,
        stack: error.stack,
        networkOnline: navigator.onLine
      };
      
      // Send to backend error tracking with API key
      try {
        const trackingApiKey = process.env.REACT_APP_ERROR_TRACKING_KEY;
        if (trackingApiKey) {
          console.log('🔍 DEBUG: Sending error tracking data:', {
            url: `${API_BASE_URL}/error-tracking`,
            hasApiKey: !!trackingApiKey,
            errorType: error.message
          });
          
          fetch(`${API_BASE_URL}/error-tracking`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'X-Error-Tracking-Key': trackingApiKey
            },
            body: JSON.stringify(errorInfo)
          }).then(response => {
            console.log('📊 Error tracking response:', response.status);
            if (!response.ok) {
              console.warn('⚠️ Error tracking failed:', response.status, response.statusText);
            }
          }).catch(trackingError => {
            console.warn('❌ Error tracking request failed:', trackingError);
          });
        } else {
          console.warn('⚠️ No error tracking API key available');
        }
      } catch (trackingError) {
        console.warn('❌ Error tracking setup failed:', trackingError);
      }
      
      throw error;
    }
  }

  /**
   * Login user with email and password
   */
  async login(email, password) {
    // 🔒 SECURITY FIX: Removed dangerous offline fallback
    // Only allow backend authentication for security
    const url = `${this.baseURL}/login`;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // 🔒 SECURITY FIX: Removed dangerous offline login
  // Client-side authentication is a security vulnerability

  /**
   * Register new user
   */
  async register(email, password, name) {
    // 🔒 SECURITY FIX: Removed dangerous offline fallback
    // Only allow backend registration for security
    const url = `${this.baseURL}/register`;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  }

  // 🔒 SECURITY FIX: Removed dangerous offline registration
  // Client-side user registration is a security vulnerability

  /**
   * Verify JWT token and get user data
   */
  async verifyToken(token) {
    // 🔒 SECURITY FIX: Removed dangerous offline token verification
    // Only allow backend token verification for security
    const url = `${this.baseURL}/verify`;
    return await this.makeRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  /**
   * 🔒 SECURITY ENDPOINT: Verify token security (prevents fake token UI rendering)
   * This endpoint specifically validates token format and authenticity
   * Returns only validation status, not user data
   */
  async verifyTokenSecurity(token) {
    const url = `${this.baseURL}/verify-token`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const result = await response.json().catch(() => ({}));
      
      return {
        valid: response.ok && result.valid === true,
        reason: result.reason || 'unknown',
        message: result.message || 'Token validation failed',
        status: response.status,
        tokenType: result.tokenType || 'unknown'
      };
      
    } catch (error) {
      return {
        valid: false,
        reason: 'network_error',
        message: `Network error: ${error.message}`,
        status: 0,
        tokenType: 'unknown'
      };
    }
  }

  // 🔒 SECURITY FIX: Removed dangerous offline token verification
  // Client-side token validation is a security vulnerability

  /**
   * 🔄 Refresh JWT token - generates new token with fresh expiration
   * Used for persistent sessions (Spotify-like experience)
   * Only valid tokens can be refreshed
   */
  async refreshToken() {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No token to refresh');
    }

    const url = `${this.baseURL}/refresh`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Token refresh failed');
      }

      const data = await response.json();

      // Update stored token with new one
      if (data.token) {
        localStorage.setItem('authToken', data.token);
      }

      return data;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email) {
    const url = `${this.baseURL}/forgot-password`;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token, newPassword) {
    const url = `${this.baseURL}/reset-password`;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  /**
   * Update user profile
   */
  async updateProfile(userData) {
    const url = `${this.baseURL}/profile`;
    return await this.makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    const url = `${this.baseURL}/change-password`;
    return await this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  /**
   * Permanently delete the current user's account and all associated data.
   * Requires password confirmation for security.
   * NOTE: Backend endpoint - see TODO_Backend.md
   */
  async deleteAccount(password) {
    const url = `${this.baseURL}/account`;
    return await this.makeRequest(url, {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  /**
   * Markiert das Onboarding für den eingeloggten Nutzer als abgeschlossen.
   * Kein Request-Body nötig (User kommt aus dem JWT). Idempotent - mehrfacher
   * Aufruf ist unschädlich.
   */
  async completeOnboarding() {
    const url = `${this.baseURL}/onboarding-complete`;
    return await this.makeRequest(url, {
      method: 'POST',
    });
  }
}

const authApiInstance = new AuthAPI();
export default authApiInstance;